import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Badge, Input, Select, TextArea, Modal, Table, StatCard, ReportCountLink } from '../components/Common';
import EngagementDrawer from '../components/EngagementDrawer';

const ENG_TYPES = [
  { value: '', label: 'Select Type' },
  { value: 'Network Penetration Test', label: 'Network Penetration Test' },
  { value: 'Network Segmentation', label: 'Network Segmentation' },
  { value: 'Application Penetration Test', label: 'Application Penetration Test' },
  { value: 'Mobile Application Test', label: 'Mobile Application Test' },
  { value: 'Red Team', label: 'Red Team' },
  { value: 'API Penetration Test', label: 'API Penetration Test' },
];
// Mirrors NETWORK_SCOPE_TYPES in backend/services/engagement-types.js. These
// types scope themselves from the Networks inventory rather than from an
// application, so the form must not demand one. Four branches below used to
// test the single string 'Network Penetration Test'; adding a second network
// type without this would have made Network Segmentation require an
// application it can never have, and hide the name field it needs.
const NETWORK_SCOPE_TYPES = ['Network Penetration Test', 'Network Segmentation'];
const usesNetworkScope = (t) => NETWORK_SCOPE_TYPES.includes(t);

const ENV_TYPES = [
  { value: '', label: 'Select Environment' },
  { value: 'Production', label: 'Production' },
  { value: 'DEV', label: 'DEV' },
  { value: 'Test', label: 'Test' },
  { value: 'UAE', label: 'UAE' },
  { value: 'Other', label: 'Other' },
];
const STATUSES = ['Requested', 'New', 'Scoping', 'Contract Sent', 'In Progress', 'In Review', 'Complete',
                  'In Retest', 'Retest Complete', 'On Hold', 'Cancelled'];

function isWithin5BusinessDays(dateStr) {
  if (!dateStr) return false;
  const start = new Date(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  if (start <= now) return false;
  let bizDays = 0;
  const d = new Date(now);
  while (d < start) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) bizDays++;
    if (bizDays > 5) return false;
  }
  return bizDays <= 5;
}

function isFollowUpDue(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  d.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);
  return d <= now;
}

// Light purple carries two meanings: an engagement whose start is close, and
// one waiting on a contract. Both say "nothing is happening yet, and something
// needs to", which is why they share a colour rather than competing for two
// near-identical purples.
const LIGHT_PURPLE = 'rgba(139, 92, 246, 0.15)';

const ENGAGEMENT_ROW_STYLE = (row) => {
  if (isFollowUpDue(row.follow_up_date) && row.status !== 'Complete' && row.status !== 'Cancelled') {
    return { background: 'rgba(250, 204, 21, 0.3)' };  // Bright yellow — follow-up due
  }
  if (row.status !== 'In Progress' && row.status !== 'Complete' && row.status !== 'Cancelled' && row.status !== 'On Hold' && isWithin5BusinessDays(row.start_date)) {
    return { background: LIGHT_PURPLE };  // Approaching start
  }
  switch (row.status) {
    case 'Contract Sent': return { background: LIGHT_PURPLE };
    case 'In Progress': return { background: 'rgba(59, 130, 246, 0.12)' };  // Blue
    case 'Complete':    return { background: 'rgba(34, 197, 94, 0.12)' };   // Green
    case 'In Retest':   return { background: 'rgba(20, 184, 166, 0.12)' };  // Teal
    case 'Retest Complete': return { background: 'rgba(16, 185, 129, 0.12)' }; // Emerald
    case 'On Hold':     return { background: 'rgba(249, 115, 22, 0.12)' };  // Orange
    case 'Cancelled':   return { background: 'rgba(239, 68, 68, 0.12)' };  // Red
    default:            return {};
  }
};
const APP_TYPES = ['Application Penetration Test', 'API Penetration Test', 'Mobile Application Test'];
const YES_NO = [{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }];
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };
const TIME_ZONES = [
  { value: '', label: 'Select Time Zone' },
  { value: 'US/Eastern', label: 'US/Eastern (ET)' },
  { value: 'US/Central', label: 'US/Central (CT)' },
  { value: 'US/Mountain', label: 'US/Mountain (MT)' },
  { value: 'US/Pacific', label: 'US/Pacific (PT)' },
  { value: 'US/Alaska', label: 'US/Alaska (AKT)' },
  { value: 'US/Hawaii', label: 'US/Hawaii (HT)' },
  { value: 'UTC', label: 'UTC' },
];
const DEFAULT_RESTRICTIONS = { noRestriction: true, timeZone: '', monday: { start: '', end: '' }, tuesday: { start: '', end: '' }, wednesday: { start: '', end: '' }, thursday: { start: '', end: '' }, friday: { start: '', end: '' }, saturday: { start: '', end: '' }, sunday: { start: '', end: '' } };

const SCOPE_TYPES = ['URL', 'IP Address', 'API Endpoint', 'CIDR Range', 'Domain', 'Mobile App'];

// Color map for engagement type pills
const TYPE_COLORS = {
  'Network Penetration Test': { bg: '#1e3a5f', color: '#93c5fd' },
  'Network Segmentation': { bg: '#1e3a5f', color: '#93c5fd' },
  'Application Penetration Test': { bg: '#4c1d95', color: '#c4b5fd' },
  'Mobile Application Test': { bg: '#065f46', color: '#6ee7b7' },
  'Red Team': { bg: '#991b1b', color: '#fca5a5' },
  'API Penetration Test': { bg: '#92400e', color: '#fed7aa' },
};
const TYPE_SHORT = {
  'Network Penetration Test': 'Network PT',
  'Network Segmentation': 'Net Seg',
  'Application Penetration Test': 'App PT',
  'Mobile Application Test': 'Mobile PT',
  'Red Team': 'Red Team',
  'API Penetration Test': 'API PT',
};

// Everything on an engagement that can be exported, in groups so the picker is
// readable. Vulnerabilities and attachments are deliberately absent — the
// finding and scope entries here are counts, not the records themselves.
const yesNo = v => (v === true ? 'Yes' : v === false ? 'No' : '');
const asDate = v => (v ? String(v).split('T')[0] : '');
const asText = v => (v == null ? '' : String(v));
const restrictions = (v) => {
  if (!v) return 'No restriction';
  if (typeof v === 'string') return v;
  if (v.noRestriction) return 'No restriction';
  const days = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday']
    .filter(d => v[d] && (v[d].start || v[d].end))
    .map(d => `${d[0].toUpperCase()}${d.slice(1)} ${v[d].start || '?'}-${v[d].end || '?'}`);
  return [v.timeZone, ...days].filter(Boolean).join('; ');
};

const EXPORT_FIELD_GROUPS = [
  { label: 'Engagement', fields: [
    { key: 'name', label: 'Name', get: e => asText(e.name) },
    { key: 'client_name', label: 'Client', get: e => asText(e.client_name) },
    { key: 'engagement_type', label: 'Type', get: e => asText(e.engagement_type) },
    { key: 'status', label: 'Status', get: e => asText(e.status) },
    { key: 'tester_name', label: 'Tester', get: e => asText(e.tester_name) },
    { key: 'requested_by_name', label: 'Requested By', get: e => asText(e.requested_by_name) },
    { key: 'environment_type', label: 'Environment', get: e => asText(e.environment_type) },
    { key: 'start_date', label: 'Start Date', get: e => asDate(e.start_date) },
    { key: 'end_date', label: 'End Date', get: e => asDate(e.end_date) },
    { key: 'follow_up_date', label: 'Follow Up Date', get: e => asDate(e.follow_up_date) },
  ]},
  { label: 'Classification', fields: [
    { key: 'crest_required', label: 'CREST Required', get: e => yesNo(e.crest_required) },
    { key: 'pci_required', label: 'PCI Required', get: e => yesNo(e.pci_required) },
    { key: 'third_party', label: 'Third Party', get: e => yesNo(e.third_party) },
    { key: 'client_obligation', label: 'Client Obligation', get: e => yesNo(e.client_obligation) },
    { key: 'client_obligation_for', label: 'Client This Test is for', get: e => asText(e.client_obligation_for) },
  ]},
  { label: 'Application scoping', fields: [
    { key: 'company_owned', label: 'Company Owned', get: e => yesNo(e.company_owned) },
    { key: 'company_hosted', label: 'Company Hosted', get: e => yesNo(e.company_hosted) },
    { key: 'internet_facing', label: 'Internet Facing', get: e => yesNo(e.internet_facing) },
    { key: 'payment_function', label: 'Payment / Checkout', get: e => yesNo(e.payment_function) },
    { key: 'file_upload_function', label: 'File Upload', get: e => yesNo(e.file_upload_function) },
    { key: 'ip_whitelist_needed', label: 'IP Whitelist Needed', get: e => yesNo(e.ip_whitelist_needed) },
    { key: 'testing_restrictions', label: 'Testing Restrictions', get: e => restrictions(e.testing_restrictions) },
    { key: 'scoping_comments', label: 'Scoping Comments', get: e => asText(e.scoping_comments) },
  ]},
  { label: 'Contacts and references', fields: [
    { key: 'customer_tracking_id', label: 'Tracking ID', get: e => asText(e.customer_tracking_id) },
    { key: 'service_order', label: 'Service Order', get: e => asText(e.service_order) },
    { key: 'jira_project_key', label: 'Jira Project', get: e => asText(e.jira_project_key) },
    { key: 'single_point_of_contact', label: 'Single Point of Contact', get: e => asText(e.single_point_of_contact) },
    { key: 'technical_manager', label: 'Technical Manager', get: e => asText(e.technical_manager) },
    { key: 'technical_manager_email', label: 'Technical Manager Email', get: e => asText(e.technical_manager_email) },
    { key: 'challenges_details', label: 'Challenges', get: e => asText(e.challenges_details) },
    { key: 'notes', label: 'Notes', get: e => asText(e.notes) },
  ]},
  { label: 'Commercial', fields: [
    { key: 'billing_amount', label: 'Billing Amount', get: e => (e.billing_amount == null ? '' : String(e.billing_amount)) },
    { key: 'sow_number', label: 'SoW Number', get: e => asText(e.sow_number) },
    { key: 'sow_credits', label: 'SoW Credits', get: e => asText(e.sow_credits) },
  ]},
  { label: 'Counts and audit', fields: [
    { key: 'finding_count', label: 'Findings (count)', get: e => asText(e.finding_count) },
    { key: 'scope_count', label: 'Scope Items (count)', get: e => asText(e.scope_count) },
    { key: 'report_count', label: 'Reports (count)', get: e => asText(e.report_count) },
    { key: 'created_by_name', label: 'Created By', get: e => asText(e.created_by_name) },
    { key: 'created_at', label: 'Created At', get: e => asDate(e.created_at) },
    { key: 'updated_at', label: 'Updated At', get: e => asDate(e.updated_at) },
  ]},
  { label: 'Identifiers', fields: [
    { key: 'id', label: 'Engagement ID', get: e => asText(e.id) },
    { key: 'client_id', label: 'Client ID', get: e => asText(e.client_id) },
    { key: 'application_id', label: 'Application ID', get: e => asText(e.application_id) },
    { key: 'sow_id', label: 'SoW ID', get: e => asText(e.sow_id) },
  ]},
];

const ALL_EXPORT_FIELDS = EXPORT_FIELD_GROUPS.flatMap(g => g.fields);

// Ticked when the dialog first opens: the columns most people want, without the
// long free-text or the raw identifiers.
const DEFAULT_EXPORT_KEYS = ['name', 'client_name', 'engagement_type', 'status', 'tester_name',
  'start_date', 'end_date', 'environment_type', 'third_party', 'customer_tracking_id', 'finding_count'];

/** RFC 4180: wrap in quotes when the value contains a quote, comma or newline. */
function escapeCsv(value) {
  const v = value == null ? '' : String(value);
  return /[",\n\r]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v;
}

const FILTER_STORAGE_KEY = 'engagements_filter';

function loadSavedFilter() {
  try {
    const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch {}
  return { status: '__active__', type: '', client: '', year: '', search: '',
           thirdParty: '', sowId: '' };
}

function EngagementNotesExpand({ row, apiFetch, user, hasGroup, readOnly }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState('');
  const loaded = useRef(false);

  useEffect(() => {
    if (!loaded.current) {
      loaded.current = true;
      apiFetch(`/engagements/${row.id}/notes`).then(r => r.ok ? r.json() : []).then(setNotes).catch(() => {});
    }
  }, [row.id, apiFetch]);

  const addNote = async () => {
    if (!text.trim()) return;
    const res = await apiFetch(`/engagements/${row.id}/notes`, { method: 'POST', body: JSON.stringify({ content: text }) });
    if (res.ok) {
      setText('');
      const r = await apiFetch(`/engagements/${row.id}/notes`);
      if (r.ok) setNotes(await r.json());
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    await apiFetch(`/engagements/${row.id}/notes/${noteId}`, { method: 'DELETE' });
    const r = await apiFetch(`/engagements/${row.id}/notes`);
    if (r.ok) setNotes(await r.json());
  };

  return (
    <div style={{ maxWidth: 900 }} onClick={e => e.stopPropagation()}>
      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Notes</label>
      {!readOnly && (
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <input value={text} onChange={e => setText(e.target.value)} placeholder="Add a note..."
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNote(); } }}
          style={{ flex: 1, padding: '6px 10px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text)', fontFamily: 'inherit' }} />
        <button onClick={addNote} disabled={!text.trim()}
          style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', background: 'var(--primary)', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap', opacity: text.trim() ? 1 : 0.5 }}>Add</button>
      </div>
      )}
      {notes.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {notes.map(note => (
            <div key={note.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '6px 10px', background: 'var(--bg-card)', borderRadius: 6, fontSize: 13 }}>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 600, fontSize: 12 }}>{note.author_name || note.author_username}</span>
                <span style={{ color: 'var(--text-secondary)', fontSize: 11, marginLeft: 8 }}>{new Date(note.created_at).toLocaleString()}</span>
                <div style={{ marginTop: 2, whiteSpace: 'pre-wrap' }}>{note.content}</div>
              </div>
              {(note.created_by === user?.id || hasGroup('Super Admins')) && (
                <button onClick={() => deleteNote(note.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 14, padding: '0 4px', flexShrink: 0 }}>×</button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>No notes yet.</p>
      )}
    </div>
  );
}

export default function Engagements() {
  const { apiFetch, user, hasGroup, isReadOnlyViewer } = useAuth();
  const [engagements, setEngagements] = useState([]);
  const [clients, setClients] = useState([]);
  const [testers, setTesters] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState(loadSavedFilter);
  const [form, setForm] = useState({ engagementType: 'Application Penetration Test', testingRestrictions: { ...DEFAULT_RESTRICTIONS }, scopeItems: [] });
  const [scopeForm, setScopeForm] = useState({ scopeType: '', value: '', description: '' });
  const [showImport, setShowImport] = useState(false);
  const [importClientId, setImportClientId] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [clientSows, setClientSows] = useState([]);
  const [allSows, setAllSows] = useState([]);
  const [clientApps, setClientApps] = useState([]);
  const [pendingAppId, setPendingAppId] = useState(null);
  const [clientVendors, setClientVendors] = useState([]);
  const [appsError, setAppsError] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [drawerEngagement, setDrawerEngagement] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [exportKeys, setExportKeys] = useState(DEFAULT_EXPORT_KEYS);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const appFilter = searchParams.get('app');

  const relativeDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.round((d - now) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    if (diff === -1) return 'Yesterday';
    if (diff > 0 && diff <= 14) return `In ${diff}d`;
    if (diff < 0 && diff >= -14) return `${Math.abs(diff)}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const canCreate = (hasGroup('Engagement Admins') || (!!user?.clientId && !user?.groups?.includes('Penetration Testers'))) && !isReadOnlyViewer;
  const canEdit = hasGroup('Engagement Admins');
  const isClientAdmin = user?.groups?.includes('Client Admins') && !user?.groups?.includes('Super Admins');
  const canAssignTester = canEdit || isClientAdmin;
  const isMspUser = !!user?.isMsp || user?.groups?.includes('Super Admins');
  const isBillingAdmin = hasGroup('MSP Billing Admins') || hasGroup('Client Billing Admins') || hasGroup('Super Admins');
  const canDelete = user?.groups?.includes('Super Admins');

  // MSP-side engagements are third-party by default — every MSP list query filters on it.
  const blankForm = useCallback(() => ({
    engagementType: 'Application Penetration Test',
    testingRestrictions: { ...DEFAULT_RESTRICTIONS },
    scopeItems: [],
    thirdParty: isMspUser,
  }), [isMspUser]);

  const openNewEngagement = () => {
    setForm(blankForm());
    setScopeForm({ scopeType: '', value: '', description: '' });
    setShowModal(true);
  };

  // Copy an application's scoping answers onto the engagement form. The
  // application lookup already returned every field, so this needs no request.
  const applyApplicationToForm = useCallback((appId, apps) => {
    const app = (apps || []).find(a => a.id === appId);
    if (!app) return;
    setForm(f => ({
      ...f, applicationId: appId,
      name: app.name, customerTrackingId: app.customer_tracking_id || '',
      pciRequired: app.pci_required ? 'true' : 'false',
      companyOwned: app.company_owned ? 'true' : 'false',
      internetFacing: app.internet_facing ? 'true' : 'false',
      companyHosted: app.company_hosted ? 'true' : 'false',
      paymentFunction: app.payment_function ? 'true' : 'false',
      ipWhitelistNeeded: app.ip_whitelist_needed ? 'true' : 'false',
      scopingComments: app.comments || '',
      scopeItems: (app.scope_items || []).map(s => ({ ...s, _key: s.id })),
    }));
  }, []);

  // "+ Test" on the Applications page links here with the client and app already
  // chosen, so open the form on arrival rather than dropping the user on the list.
  useEffect(() => {
    const newFor = searchParams.get('newFor');
    if (!newFor) return;
    const appId = searchParams.get('app');
    setForm({ ...blankForm(), clientId: newFor, applicationId: appId || '' });
    setScopeForm({ scopeType: '', value: '', description: '' });
    setPendingAppId(appId || null);
    setShowModal(true);
    // Drop the params so a refresh or a later filter change does not reopen it.
    const next = new URLSearchParams(searchParams);
    next.delete('newFor');
    next.delete('app');
    setSearchParams(next, { replace: true });
  }, []);  // mount only — the link params are consumed once

  // clientApps loads asynchronously off form.clientId, so the deep-linked
  // application can only be prefilled once that list arrives.
  useEffect(() => {
    if (!pendingAppId || clientApps.length === 0) return;
    if (clientApps.some(a => a.id === pendingAppId)) {
      applyApplicationToForm(pendingAppId, clientApps);
      setPendingAppId(null);
    }
  }, [pendingAppId, clientApps, applyApplicationToForm]);

  // Persist filter to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filter));
    setCurrentPage(0);
  }, [filter]);

  const load = useCallback(async () => {
    try {
      const [engRes, clientRes] = await Promise.all([
        apiFetch('/engagements'), apiFetch('/clients')
      ]);
      // A read-only account is refused /clients and /users/testers. An error
      // body must never reach state that gets .map()ed at render — that blanks
      // the page rather than degrading.
      const eng = await engRes.json().catch(() => null);
      setEngagements(Array.isArray(eng) ? eng : []);
      const cli = await clientRes.json().catch(() => null);
      setClients(Array.isArray(cli) ? cli : []);
      if (user?.isMsp || isClientAdmin) {
        const tRes = await apiFetch('/users/testers');
        const t = await tRes.json().catch(() => null);
        setTesters(Array.isArray(t) ? t : []);
      }
      if (isBillingAdmin) {
        const sowRes = await apiFetch('/client-sow').catch(() => null);
        const s = sowRes ? await sowRes.json().catch(() => null) : null;
        setAllSows(Array.isArray(s) ? s : []);
      }
    } catch {}
  }, [apiFetch, user]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const cId = user?.clientId || form.clientId;
    if (cId) {
      apiFetch(`/engagements/sows-for-client/${cId}`).then(r => r.json()).then(setClientSows).catch(() => setClientSows([]));
      apiFetch(`/client-vendors/${cId}`).then(r => r.json()).then(setClientVendors).catch(() => setClientVendors([]));
      // Distinguish "this client has no applications" from "the lookup failed" —
      // both used to end up as an empty list, which silently hid the dropdown.
      apiFetch(`/applications/by-client/${cId}`)
        .then(async r => {
          const d = await r.json().catch(() => null);
          if (!r.ok || !Array.isArray(d)) throw new Error(d?.error || `HTTP ${r.status}`);
          return d;
        })
        .then(d => { setClientApps(d); setAppsError(''); })
        .catch(err => { setClientApps([]); setAppsError(err.message || 'Could not load applications'); });
    } else {
      setClientSows([]);
      setClientApps([]);
      setAppsError('');
    }
  }, [form.clientId, user?.clientId, apiFetch]);

  // Add scope item to the form's pending list
  const addScopeToForm = () => {
    if (!scopeForm.scopeType || !scopeForm.value) return;
    setForm(f => ({ ...f, scopeItems: [...(f.scopeItems || []), { ...scopeForm, _key: Date.now() }] }));
    setScopeForm({ scopeType: '', value: '', description: '' });
  };

  // Remove scope item from pending list
  const removeScopeFromForm = (key) => {
    setForm(f => ({ ...f, scopeItems: (f.scopeItems || []).filter(s => s._key !== key) }));
  };

  const handleCreate = async () => {
    try {
      const isAppType = APP_TYPES.includes(form.engagementType);
      const body = {
        clientId: user?.clientId || form.clientId,
        name: form.name || null,
        engagementType: form.engagementType,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        crestRequired: form.crestRequired === 'true',
        pciRequired: form.pciRequired === 'true',
        thirdParty: form.thirdParty || false,
        vendorId: form.thirdParty ? (form.vendorId || clientVendors.find(v => v.is_msp_default)?.id || null) : null,
        clientObligation: form.clientObligation || false,
        clientObligationFor: form.clientObligation ? (form.clientObligationFor || null) : null,
        environmentType: form.environmentType || null,
        customerTrackingId: form.customerTrackingId,
        notes: form.notes,
        ...(isAppType ? {
          companyOwned: form.companyOwned === 'true',
          companyHosted: form.companyHosted === 'true',
          internetFacing: form.internetFacing === 'true',
          paymentFunction: form.paymentFunction === 'true',
          fileUploadFunction: form.fileUploadFunction === 'true',
          ipWhitelistNeeded: form.ipWhitelistNeeded === 'true',
          scopingComments: form.scopingComments || null,
        } : {}),
        testingRestrictions: form.testingRestrictions?.noRestriction ? null : form.testingRestrictions,
        jiraProjectKey: form.jiraProjectKey || null,
        sowId: form.sowId || null,
        sowCredits: parseInt(form.sowCredits) || 1,
        applicationId: form.applicationId || null,
      };
      const res = await apiFetch('/engagements', { method: 'POST', body: JSON.stringify(body) });
      if (!res.ok) { const d = await res.json(); alert(d.error); return; }
      const created = await res.json();

      // Add scope items to the newly created engagement
      if (form.scopeItems?.length > 0) {
        // Each result is checked. The engagement already exists by this point,
        // so a rejected scope item cannot be undone by failing the whole call
        // -- but it must be reported, or the item simply is not there and
        // nothing said so. Scope validation makes that a live case.
        const rejected = [];
        for (const s of form.scopeItems) {
          const r = await apiFetch(`/engagements/${created.id}/scope`, {
            method: 'POST',
            body: JSON.stringify({ scopeType: s.scopeType || s.scope_type, value: s.value, description: s.description || null })
          });
          if (!r.ok) {
            const d = await r.json().catch(() => ({}));
            rejected.push(`${s.value}: ${d.error || r.status}`);
          }
        }
        if (rejected.length) {
          alert(`The engagement was created, but ${rejected.length} scope item(s) were not added:\n\n`
                + rejected.join('\n')
                + '\n\nAdd them from the engagement once corrected.');
        }
      }

      // Copy application comments as engagement note
      if (form.applicationId && form.scopingComments) {
        await apiFetch(`/engagements/${created.id}/notes`, {
          method: 'POST',
          body: JSON.stringify({ content: `[From Application] ${form.scopingComments}` })
        });
      }

      setShowModal(false);
      setForm(blankForm());
      setScopeForm({ scopeType: '', value: '', description: '' });
      load();
    } catch (err) { alert(err.message); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await apiFetch('/engagements/import/template');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'engagement-import-template.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { alert('Failed to download template'); }
  };

  const handleImport = async () => {
    if (!importClientId || !importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('clientId', importClientId);
      const token = sessionStorage.getItem('token');
      const res = await fetch('/api/engagements/import', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (!res.ok) { setImportResult({ error: data.error }); }
      else { setImportResult(data); load(); }
    } catch (err) { setImportResult({ error: err.message }); }
    setImporting(false);
  };

  const openDelete = async (row) => {
    setDeleteTarget(row);
    setDeleteImpact(null);
    setDeleteConfirmText('');
    try {
      const res = await apiFetch(`/engagements/${row.id}/delete-impact`);
      if (res.ok) setDeleteImpact(await res.json());
      else setDeleteImpact({ error: (await res.json()).error });
    } catch (err) { setDeleteImpact({ error: err.message }); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await apiFetch(`/engagements/${deleteTarget.id}`, { method: 'DELETE' });
      if (!res.ok) { const d = await res.json(); alert(d.error || 'Delete failed'); }
      else { setDeleteTarget(null); load(); }
    } catch (err) { alert(err.message); }
    setDeleting(false);
  };

  const inlineUpdate = async (id, body) => {
    try {
      const res = await apiFetch(`/engagements/${id}`, { method: 'PUT', body: JSON.stringify(body) });
      if (!res.ok) { const d = await res.json(); alert(d.error); return; }
      load();
    } catch (err) { alert(err.message); }
  };

  // Exports exactly what the list is showing — the same filtered rows, in the
  // same order — with only the chosen columns.
  const exportCsv = () => {
    const fields = ALL_EXPORT_FIELDS.filter(f => exportKeys.includes(f.key));
    if (fields.length === 0) return;
    const header = fields.map(f => escapeCsv(f.label)).join(',');
    const body = filtered.map(e => fields.map(f => escapeCsv(f.get(e))).join(','));
    // Excel needs the BOM to read UTF-8 accented characters correctly.
    const csv = '\uFEFF' + [header, ...body].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `engagements-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setShowExport(false);
  };

  const years = [...new Set(engagements.map(e => e.end_date?.split('T')[0]?.split('-')[0]).filter(Boolean))].sort().reverse();

  // The statements of work a billing admin may filter by. Narrowed to the
  // selected client, which is the whole point: a provider-side billing admin
  // looking at one client should not be reading past forty other clients' SoW
  // numbers to find theirs.
  //
  // Built from allSows (loaded from /client-sow for billing admins) rather
  // than from the engagement rows, so a SoW that has no engagements yet still
  // appears — "which SoWs have nothing booked against them" is a question a
  // billing admin actually asks, and a list derived from rows can never
  // answer it.
  const sowOptions = React.useMemo(() => {
    const list = filter.client
      ? allSows.filter(s => s.client_id === filter.client)
      : allSows;
    return [...list].sort((a, b) =>
      String(a.client_name || '').localeCompare(String(b.client_name || ''))
      || String(a.sow_number || '').localeCompare(String(b.sow_number || ''), undefined, { numeric: true }));
  }, [allSows, filter.client]);

  // A SoW chosen under one client must not survive a switch to another, or the
  // list silently empties while the control that emptied it is off screen.
  React.useEffect(() => {
    if (!filter.sowId || filter.sowId === '__none__' || !filter.client) return;
    const chosen = allSows.find(x => String(x.id) === String(filter.sowId));
    if (chosen && String(chosen.client_id) !== String(filter.client)) {
      setFilter(f => ({ ...f, sowId: '' }));
    }
  }, [filter.client, filter.sowId, allSows]);

  const filtered = engagements.filter(e => {
    if (appFilter && e.application_id !== appFilter) return false;
    if (!appFilter && filter.status === '__active__'
        && ['Cancelled', 'Complete', 'Retest Complete'].includes(e.status)) return false;
    if (!appFilter && filter.status && filter.status !== '__active__' && e.status !== filter.status) return false;
    if (filter.type && e.engagement_type !== filter.type) return false;
    if (filter.client && e.client_id !== filter.client) return false;
    if (filter.year && e.end_date?.split('T')[0]?.split('-')[0] !== filter.year) return false;
    if (filter.thirdParty === 'yes' && !e.third_party) return false;
    if (filter.thirdParty === 'no' && e.third_party) return false;
    if (user?.groups?.includes('Super Admins') && !filter.showOtherVendors && e.vendor_id && !e.vendor_is_msp_default) return false;
    // Advanced filters
    if (filter.tester && e.tester_id !== filter.tester) return false;
    if (filter.vendor && e.vendor_name !== filter.vendor) return false;
    if (filter.environment && e.environment_type !== filter.environment) return false;
    if (filter.application && e.application_id !== filter.application) return false;
    if (filter.crest === 'yes' && !e.crest_required) return false;
    if (filter.crest === 'no' && e.crest_required) return false;
    if (filter.pci === 'yes' && !e.pci_required) return false;
    if (filter.pci === 'no' && e.pci_required) return false;
    if (filter.internetFacing === 'yes' && !e.internet_facing) return false;
    if (filter.internetFacing === 'no' && e.internet_facing) return false;
    if (filter.ipWhitelist === 'yes' && !e.ip_whitelist_needed) return false;
    if (filter.ipWhitelist === 'no' && e.ip_whitelist_needed) return false;
    if (filter.hasFindings === 'yes' && !(parseInt(e.finding_count) > 0)) return false;
    if (filter.hasFindings === 'no' && parseInt(e.finding_count) > 0) return false;
    if (filter.billed === 'yes' && !e.is_billed) return false;
    if (filter.billed === 'no' && e.is_billed) return false;
    if (filter.sow && e.sow_number !== filter.sow) return false;
    // '__none__' answers "what work is not on a statement of work?", which is
    // the first thing a billing admin reaches for and cannot be expressed by
    // picking a SoW.
    if (filter.sowId === '__none__' && e.sow_id) return false;
    if (filter.sowId && filter.sowId !== '__none__' && String(e.sow_id) !== String(filter.sowId)) return false;
    if (filter.search) {
      const q = filter.search.toLowerCase();
      const searchable = [e.client_name, e.name, e.engagement_type, e.tester_name, e.customer_tracking_id, e.status]
        .filter(Boolean).join(' ').toLowerCase();
      if (!searchable.includes(q)) return false;
    }
    return true;
  });

  const statusColors = {
    Requested: { bg: 'rgba(99,102,241,0.12)', color: '#a5b4fc' }, New: { bg: 'rgba(79,143,255,0.12)', color: '#93bbff' },
    Scoping: { bg: 'rgba(139,92,246,0.12)', color: '#c4b5fd' }, 'Contract Sent': { bg: 'rgba(6,182,212,0.12)', color: '#67e8f9' },
    'In Progress': { bg: 'rgba(79,143,255,0.12)', color: '#4f8fff' }, 'In Review': { bg: 'rgba(245,158,66,0.12)', color: '#f59e42' },
    Complete: { bg: 'rgba(52,211,153,0.12)', color: '#34d399' },
    'In Retest': { bg: 'rgba(20,184,166,0.12)', color: '#2dd4bf' },
    'Retest Complete': { bg: 'rgba(16,185,129,0.12)', color: '#10b981' },
    'On Hold': { bg: 'rgba(234,179,8,0.12)', color: '#fbbf24' },
    Cancelled: { bg: 'rgba(100,116,139,0.12)', color: '#94a3b8' },
  };
  const statusPillSelect = (status) => {
    const c = statusColors[status] || { bg: '#f1f5f9', color: '#475569' };
    return { background: c.bg, color: c.color, border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: '3px 10px 3px 10px', borderRadius: 12, outline: 'none', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', letterSpacing: '0.01em', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(c.color)}' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center', paddingRight: 22 };
  };
  const pillSelect = { background: 'rgba(100,116,139,0.1)', color: 'var(--text-secondary)', border: 'none', fontSize: 11, fontWeight: 500, cursor: 'pointer', padding: '3px 10px', borderRadius: 12, outline: 'none', maxWidth: 150, fontFamily: 'inherit' };
  const optionStyle = { color: '#111', background: '#fff' };
  const datePillStyle = (hasValue) => ({
    background: hasValue ? 'rgba(79,143,255,0.08)' : 'var(--bg-input)', color: hasValue ? '#4f8fff' : 'var(--text-muted)',
    border: hasValue ? '1px solid rgba(79,143,255,0.2)' : '1px solid var(--border)', fontSize: 11, fontWeight: hasValue ? 500 : 400,
    cursor: 'pointer', padding: '3px 8px', borderRadius: 8, display: 'inline-block', whiteSpace: 'nowrap',
  });
  const DatePill = React.memo(({ value, onChange }) => {
    const [editing, setEditing] = React.useState(false);
    const inputRef = React.useRef(null);
    const blurTimer = React.useRef(null);
    React.useEffect(() => { if (editing && inputRef.current) { inputRef.current.focus(); try { inputRef.current.showPicker(); } catch {} } }, [editing]);
    React.useEffect(() => () => clearTimeout(blurTimer.current), []);
    if (editing) {
      return <input ref={inputRef} type="date" value={value || ''} style={{ fontSize: 11, padding: '3px 6px', borderRadius: 8, border: '1px solid var(--primary)', background: 'var(--bg-input)', color: 'var(--text)', outline: 'none', width: 120, fontFamily: 'inherit' }}
        onClick={e => e.stopPropagation()}
        onChange={e => { onChange(e.target.value || null); setEditing(false); }}
        onBlur={() => { blurTimer.current = setTimeout(() => setEditing(false), 300); }}
        onFocus={() => clearTimeout(blurTimer.current)} />;
    }
    return <span style={datePillStyle(!!value)} onClick={e => { e.stopPropagation(); setEditing(true); }}>{value || 'Set date'}</span>;
  });

  const columns = [
    ...(isMspUser ? [{ key: 'client_name', label: 'Client' }] : [
      { key: 'vendor_name', label: 'Testing Firm', render: (v, row) => {
        if (!row.third_party || !v) return <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Internal</span>;
        return <span style={{ fontSize: 12, fontWeight: 500 }}>{v}</span>;
      }},
    ]),
    { key: 'name', label: 'Name', render: v => v || '—' },
    { key: 'engagement_type', label: 'Type', render: (v) => (
      <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
        {TYPE_SHORT[v] || v}
      </span>
    )},
    { key: 'status', label: 'Status', render: (v, row) => canEdit ? (
      <select value={v} className="pill-select" style={statusPillSelect(v)} onClick={e => e.stopPropagation()}
        onChange={e => { e.stopPropagation(); inlineUpdate(row.id, { status: e.target.value }); }}>
        {STATUSES.map(s => {
          const isTechMgr = hasGroup('Technical Managers') || hasGroup('Super Admins');
          const disabled = (s === 'Cancelled' && !isTechMgr)
            || (s === 'Complete' && row.status === 'In Review' && !isTechMgr)
            || (s === 'Retest Complete' && !isTechMgr)
            || (s === 'In Retest' && s !== row.status && !['Complete', 'Retest Complete'].includes(row.status));
          const why = (s === 'In Retest' && s !== row.status && !['Complete', 'Retest Complete'].includes(row.status))
            ? ' (from Complete only)' : ' (Technical Manager)';
          return <option key={s} value={s} style={optionStyle} disabled={disabled}>{s}{disabled ? why : ''}</option>;
        })}
      </select>
    ) : <Badge>{v}</Badge> },
    { key: 'tester_id', label: 'Tester', render: (v, row) => {
      const name = row.tester_name || '';
      const initials = name ? name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() : '';
      const colors = ['#3b82f6','#8b5cf6','#06b6d4','#f59e0b','#ec4899','#10b981','#f97316'];
      const avatarColor = name ? colors[name.charCodeAt(0) % colors.length] : '#94a3b8';
      const avatarChip = (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 22, height: 22, borderRadius: '50%', background: name ? avatarColor : 'var(--bg-input)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: 'white', flexShrink: 0, border: name ? 'none' : '1px dashed var(--border)' }}>
            {initials || <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}
          </div>
          <span style={{ fontSize: 12, color: name ? 'var(--text)' : 'var(--text-muted)' }}>{name || 'Unassigned'}</span>
        </div>
      );
      if (canAssignTester) {
        return (
          <div style={{ position: 'relative', cursor: 'pointer' }} onClick={e => e.stopPropagation()}>
            {avatarChip}
            <select value={v || ''} className="pill-select"
              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }}
              onChange={e => { e.stopPropagation(); inlineUpdate(row.id, { testerId: e.target.value || null }); }}>
              <option value="" style={optionStyle}>Unassigned</option>
              {testers.map(t => <option key={t.id} value={t.id} style={optionStyle}>{t.full_name || t.username}</option>)}
            </select>
          </div>
        );
      }
      return avatarChip;
    } },
    { key: 'start_date', label: 'Start', render: (v, row) => canEdit ? (
      <DatePill value={v?.split('T')[0]} onChange={val => inlineUpdate(row.id, { startDate: val })} />
    ) : v ? <span title={v.split('T')[0]} style={datePillStyle(true)}>{relativeDate(v)}</span> : <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span> },
    { key: 'end_date', label: 'End', render: (v, row) => canEdit ? (
      <DatePill value={v?.split('T')[0]} onChange={val => inlineUpdate(row.id, { endDate: val })} />
    ) : v ? <span title={v.split('T')[0]} style={datePillStyle(true)}>{relativeDate(v)}</span> : <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span> },
    { key: 'finding_count', label: 'Findings', render: v => {
      const n = v || 0;
      const bg = n > 10 ? 'rgba(239,68,68,0.12)' : n > 0 ? 'rgba(245,158,66,0.12)' : 'rgba(100,116,139,0.08)';
      const color = n > 10 ? '#ef4444' : n > 0 ? '#f59e42' : 'var(--text-muted)';
      return <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 24, height: 22, borderRadius: 6, fontSize: 12, fontWeight: 700, background: bg, color, padding: '0 6px' }}>{n}</span>;
    } },
    { key: 'report_count', label: 'Reports', render: (v, row) => (
      <ReportCountLink count={v} engagementId={row.id} navigate={navigate} />
    ) },
    ...(hasGroup('Engagement Admins') ? [{ key: 'follow_up_date', label: 'Follow Up', render: (v, row) => (
      <DatePill value={v?.split('T')[0]} onChange={val => inlineUpdate(row.id, { followUpDate: val })} />
    ) }] : []),
    ...(hasGroup('Super Admins') ? [{ key: 'third_party', label: '3rd Party', render: (v, row) => canEdit ? (
      <input type="checkbox" checked={v || false} onClick={e => e.stopPropagation()}
        onChange={e => { e.stopPropagation(); inlineUpdate(row.id, { thirdParty: e.target.checked }); }}
        style={{ cursor: 'pointer', width: 16, height: 16 }} />
    ) : (v ? '✓' : '—') }] : []),
    ...(user?.isMsp ? [{ key: 'sow_number', label: 'SoW', render: (v, row) => {
      const locked = row.is_billed && !['New','Scoping','In Progress','Requested'].includes(row.status);
      if (!isBillingAdmin || locked) {
        return v ? <span style={{ fontSize: 11, fontWeight: 500, background: 'rgba(79,143,255,0.08)', color: '#4f8fff', padding: '3px 10px', borderRadius: 12 }}>{v}</span> : <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>;
      }
      // What a SoW can be newly assigned TO: this client's active ones. A
      // completed statement of work should not collect new engagements.
      const assignable = allSows.filter(s => s.client_id === row.client_id && s.status === 'Active');

      // What the cell must be able to SHOW is a different question, and
      // conflating the two was a bug. Marking a SoW complete dropped it out of
      // the list above, and a <select> whose value matches no option renders
      // as its first option — so every engagement on that SoW read "— None —"
      // in this column while the SoW's own view still listed all of them. The
      // assignment was never lost; the column misreported it.
      //
      // So the current assignment is always present as an option, whatever its
      // status, and falls back to the sow_number the list query already joins
      // in if the SoW is missing from allSows entirely.
      const options = [...assignable];
      if (row.sow_id && !options.some(o => String(o.id) === String(row.sow_id))) {
        const known = allSows.find(o => String(o.id) === String(row.sow_id));
        options.push({
          id: row.sow_id,
          sow_number: known?.sow_number || row.sow_number || 'Assigned SoW',
          status: known?.status || '',
          inactive: true,
        });
      }
      return (
        <select value={row.sow_id || ''} className="pill-select" onClick={e => e.stopPropagation()}
          onChange={e => { e.stopPropagation(); inlineUpdate(row.id, { sowId: e.target.value || null }); }}
          title={row.sow_number ? `Statement of work ${row.sow_number}` : 'No statement of work assigned'}
          style={{ ...pillSelect, background: row.sow_id ? 'rgba(79,143,255,0.08)' : 'rgba(100,116,139,0.1)', color: row.sow_id ? '#4f8fff' : 'var(--text-muted)', minWidth: 90, maxWidth: 150 }}>
          <option value="">— None —</option>
          {options.map(s => (
            <option key={s.id} value={s.id}>
              {/* Credits remaining is the number that matters while a SoW is
                  live. Once it is closed the status is, so the label says
                  which rather than printing "undefined left". */}
              {s.inactive
                ? `${s.sow_number}${s.status ? ` (${s.status})` : ''}`
                : `${s.sow_number} (${s.remaining_credits} left)`}
            </option>
          ))}
        </select>
      );
    } }] : []),
    ...(canDelete ? [{ key: 'id', label: '', render: (v, row) => (
      <Button variant="danger" style={{ padding: '4px 10px', fontSize: 12 }}
        onClick={e => { e.stopPropagation(); openDelete(row); }}>Delete</Button>
    ) }] : []),
  ];

  const statCounts = React.useMemo(() => {
    const all = engagements || [];
    return {
      total: all.length,
      inProgress: all.filter(e => e.status === 'In Progress').length,
      inReview: all.filter(e => e.status === 'In Review').length,
      complete: all.filter(e => e.status === 'Complete').length,
    };
  }, [engagements]);

  return (
    <div style={{ maxWidth: '100%', width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.3px' }}>Engagements</h1>
            {appFilter && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', background: 'var(--primary)', color: '#fff', borderRadius: 16, fontSize: 12, fontWeight: 600 }}>
                Filtered by application
                <button onClick={() => setSearchParams({})} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
              </span>
            )}
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>Manage penetration testing engagements across all clients</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" onClick={() => setShowExport(true)} style={{ fontSize: 13, padding: '9px 16px' }}>Export CSV</Button>
          {canEdit && <Button variant="secondary" onClick={() => { setShowImport(true); setImportResult(null); setImportFile(null); setImportClientId(''); }} style={{ fontSize: 13, padding: '9px 16px' }}>Import CSV</Button>}
          {canCreate && <Button onClick={openNewEngagement} style={{ fontSize: 13, fontWeight: 600, padding: '9px 16px' }}>+ New Engagement</Button>}
        </div>
      </div>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16, marginBottom: 20 }}>
        <StatCard label="Total" value={statCounts.total} color="var(--text)"
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>}
          iconBg="var(--bg-input)" />
        <StatCard label="In Progress" value={statCounts.inProgress} color="#4f8fff"
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4f8fff" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>}
          iconBg="rgba(79,143,255,0.1)" />
        <StatCard label="In Review" value={statCounts.inReview} color="#f59e42"
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e42" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>}
          iconBg="rgba(245,158,66,0.1)" />
        <StatCard label="Complete" value={statCounts.complete} color="#34d399"
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
          iconBg="rgba(52,211,153,0.1)" />
      </div>

      {/* Quick Filter Chips */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '0 1 260px' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input placeholder="Search engagements..." value={filter.search}
            onChange={e => setFilter(f => ({ ...f, search: e.target.value }))}
            style={{ width: '100%', padding: '7px 10px 7px 32px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, background: 'var(--bg-card)', color: 'var(--text)', outline: 'none', fontFamily: 'inherit' }} />
        </div>

        <div style={{ display: 'flex', gap: 4, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: 3 }}>
          {[
            { value: '__active__', label: 'All Active' },
            { value: 'In Progress', label: 'In Progress', count: statCounts.inProgress },
            { value: 'In Review', label: 'In Review', count: statCounts.inReview },
            { value: 'Complete', label: 'Complete', count: statCounts.complete },
            { value: '', label: 'All' },
          ].map(chip => (
            <button key={chip.value} onClick={() => setFilter(f => ({ ...f, status: chip.value }))}
              style={{
                padding: '5px 12px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: filter.status === chip.value ? 600 : 500,
                background: filter.status === chip.value ? 'var(--primary)' : 'transparent',
                color: filter.status === chip.value ? 'white' : 'var(--text-secondary)',
                cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4, transition: 'all 0.15s'
              }}>
              {chip.label}
              {chip.count != null && <span style={{ fontSize: 10, opacity: 0.8, fontWeight: 700 }}>{chip.count}</span>}
            </button>
          ))}
        </div>

        <select value={filter.type} onChange={e => setFilter(f => ({ ...f, type: e.target.value }))}
          style={{ padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, background: 'var(--bg-card)', color: filter.type ? 'var(--primary)' : 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit' }}>
          <option value="">All Types</option>
          {ENG_TYPES.slice(1).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>

        {(user?.isMsp || user?.groups?.includes('Super Admins')) && (
          <select value={filter.client} onChange={e => setFilter(f => ({ ...f, client: e.target.value }))}
            style={{ padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, background: 'var(--bg-card)', color: filter.client ? 'var(--primary)' : 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit' }}>
            <option value="">All Clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}

        {/* Billing admins filter by statement of work. Sits immediately after
            the client filter because it is scoped by it: choosing a client
            narrows these options to that client's SoWs. */}
        {isBillingAdmin && (
          <select value={filter.sowId} onChange={e => setFilter(f => ({ ...f, sowId: e.target.value }))}
            title={filter.client
              ? "Statements of work for the selected client"
              : "All statements of work. Choose a client to narrow this list."}
            style={{ padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, background: 'var(--bg-card)', color: filter.sowId ? 'var(--primary)' : 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit', maxWidth: 260 }}>
            <option value="">All SoWs</option>
            <option value="__none__">No SoW assigned</option>
            {sowOptions.map(sw => (
              <option key={sw.id} value={sw.id}>
                {/* The client name is only informative when the list spans
                    more than one; sow_number is unique per client, not
                    globally, so without it two rows can read identically. */}
                {(!filter.client && sw.client_name ? `${sw.client_name} \u2014 ` : '')
                  + sw.sow_number
                  + (sw.status && sw.status !== 'Active' ? ` (${sw.status})` : '')}
              </option>
            ))}
          </select>
        )}

        <select value={filter.year} onChange={e => setFilter(f => ({ ...f, year: e.target.value }))}
          style={{ padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, background: 'var(--bg-card)', color: filter.year ? 'var(--primary)' : 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit' }}>
          <option value="">All Years</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        {user?.groups?.includes('Super Admins') && (
          <select value={filter.thirdParty} onChange={e => setFilter(f => ({ ...f, thirdParty: e.target.value }))}
            style={{ padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, background: 'var(--bg-card)', color: filter.thirdParty ? 'var(--primary)' : 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit' }}>
            <option value="">All Engagements</option>
            <option value="yes">Third Party Only</option>
            <option value="no">Non-Third Party</option>
          </select>
        )}
        {user?.groups?.includes('Super Admins') && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer', padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 8, background: filter.showOtherVendors ? 'rgba(79,143,255,0.08)' : 'var(--bg-card)' }}>
            <input type="checkbox" checked={filter.showOtherVendors || false}
              onChange={e => setFilter(f => ({ ...f, showOtherVendors: e.target.checked }))} />
            Show other vendors
          </label>
        )}

        <button onClick={() => setShowAdvanced(!showAdvanced)}
          style={{
            padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, fontWeight: 500,
            background: showAdvanced ? 'var(--primary-light)' : 'var(--bg-card)',
            color: showAdvanced ? 'var(--primary)' : 'var(--text-secondary)',
            cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5, marginLeft: 'auto'
          }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
          Advanced
        </button>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvanced && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Advanced Filters</span>
            <button onClick={() => setFilter(f => ({ ...f, tester: '', crest: '', pci: '', internetFacing: '', ipWhitelist: '', environment: '', application: '', hasFindings: '', billed: '', vendor: '', createdBy: '', sow: '' }))}
              style={{ background: 'none', border: 'none', fontSize: 12, color: 'var(--primary)', cursor: 'pointer', fontFamily: 'inherit' }}>Clear all</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
            {(() => {
              const sel = (label, key, options) => (
                <div key={key}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>{label}</div>
                  <select value={filter[key] || ''} onChange={e => setFilter(f => ({ ...f, [key]: e.target.value }))}
                    style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, background: 'var(--bg-input)', color: filter[key] ? 'var(--primary)' : 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'inherit' }}>
                    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              );
              const yesNo = (label, key) => sel(label, key, [{ value: '', label: 'Any' }, { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]);
              return (
                <>
                  {sel('Tester', 'tester', [{ value: '', label: 'All Testers' }, ...testers.map(t => ({ value: t.id, label: t.full_name || t.username }))])}
                  {sel('Vendor', 'vendor', [{ value: '', label: 'All Vendors' }, ...(() => { const seen = new Set(); return engagements.filter(e => e.vendor_name && !seen.has(e.vendor_name) && seen.add(e.vendor_name)).map(e => ({ value: e.vendor_name, label: e.vendor_name })); })()])}
                  {sel('Environment', 'environment', [{ value: '', label: 'All Environments' }, ...['Production', 'DEV', 'Test', 'UAT', 'Other'].map(v => ({ value: v, label: v }))])}
                  {sel('Application', 'application', [{ value: '', label: 'All Applications' }, ...(() => { const seen = new Set(); return engagements.filter(e => e.application_name && !seen.has(e.application_id) && seen.add(e.application_id)).map(e => ({ value: e.application_id, label: e.application_name })); })()])}
                  {/* Billing admins get the proper SoW filter in the main bar,
                      keyed on sow_id and scoped by client. This row-derived one
                      remains for everyone else, who do not load the SoW list. */}
                  {!isBillingAdmin && sel('Statement of Work', 'sow', [{ value: '', label: 'All SoWs' }, ...(() => { const seen = new Set(); return engagements.filter(e => e.sow_number && !seen.has(e.sow_number) && seen.add(e.sow_number)).map(e => ({ value: e.sow_number, label: e.sow_number })); })()])}
                  {yesNo('CREST Required', 'crest')}
                  {yesNo('PCI Required', 'pci')}
                  {yesNo('Internet Facing', 'internetFacing')}
                  {yesNo('IP Whitelist Needed', 'ipWhitelist')}
                  {yesNo('Has Findings', 'hasFindings')}
                  {isBillingAdmin && yesNo('Billed', 'billed')}
                </>
              );
            })()}
          </div>
        </div>
      )}

      <Table columns={columns} data={filtered.slice(currentPage * pageSize, (currentPage + 1) * pageSize)}
        onRowClick={row => setDrawerEngagement(row)}
        onRowDoubleClick={row => navigate(`/engagements/${row.id}`)}
        rowStyle={row => ({
          ...ENGAGEMENT_ROW_STYLE(row),
          ...(drawerEngagement?.id === row.id ? { background: 'var(--primary-light, rgba(59,130,246,0.06))' } : {})
        })}
      />
      {filtered.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', fontSize: 13, color: 'var(--text-secondary)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>Show</span>
            <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(0); }}
              style={{ padding: '4px 8px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text)', fontSize: 13 }}>
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span>per page</span>
            <span style={{ marginLeft: 8 }}>({filtered.length} total)</span>
          </div>
          {Math.ceil(filtered.length / pageSize) > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0}
                style={{ padding: '4px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: currentPage === 0 ? 'var(--bg-input)' : 'var(--bg-card)', color: 'var(--text)', cursor: currentPage === 0 ? 'default' : 'pointer', opacity: currentPage === 0 ? 0.5 : 1 }}>Prev</button>
              <span>Page {currentPage + 1} of {Math.ceil(filtered.length / pageSize)}</span>
              <button onClick={() => setCurrentPage(p => Math.min(Math.ceil(filtered.length / pageSize) - 1, p + 1))} disabled={currentPage >= Math.ceil(filtered.length / pageSize) - 1}
                style={{ padding: '4px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', background: currentPage >= Math.ceil(filtered.length / pageSize) - 1 ? 'var(--bg-input)' : 'var(--bg-card)', color: 'var(--text)', cursor: currentPage >= Math.ceil(filtered.length / pageSize) - 1 ? 'default' : 'pointer', opacity: currentPage >= Math.ceil(filtered.length / pageSize) - 1 ? 0.5 : 1 }}>Next</button>
            </div>
          )}
        </div>
      )}

      <EngagementDrawer engagement={drawerEngagement} onClose={() => setDrawerEngagement(null)} />

      <Modal isOpen={showExport} onClose={() => setShowExport(false)} title="Export Engagements to CSV" width={720}>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 4 }}>
          Exports the <strong>{filtered.length}</strong> engagement{filtered.length === 1 ? '' : 's'} currently
          matching your filters, not the whole list.
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14 }}>
          Vulnerabilities and attachments are not included; the counts below are totals only.
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
            onClick={() => setExportKeys(ALL_EXPORT_FIELDS.map(f => f.key))}>Select all</Button>
          <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
            onClick={() => setExportKeys([])}>Clear</Button>
          <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
            onClick={() => setExportKeys(DEFAULT_EXPORT_KEYS)}>Reset</Button>
          <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-secondary)', alignSelf: 'center' }}>
            {exportKeys.length} column{exportKeys.length === 1 ? '' : 's'} selected
          </div>
        </div>

        <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 12 }}>
          {EXPORT_FIELD_GROUPS.map(group => (
            <div key={group.label} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
                            color: 'var(--text-secondary)', marginBottom: 6 }}>{group.label}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
                {group.fields.map(f => (
                  <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={exportKeys.includes(f.key)}
                      onChange={e => setExportKeys(prev => e.target.checked
                        ? [...prev, f.key]
                        : prev.filter(k => k !== f.key))} />
                    {f.label}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="secondary" onClick={() => setShowExport(false)}>Cancel</Button>
          <Button onClick={exportCsv} disabled={exportKeys.length === 0 || filtered.length === 0}>
            Export {filtered.length} engagement{filtered.length === 1 ? '' : 's'}
          </Button>
        </div>
      </Modal>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="New Engagement" width={700}>
        {!user?.clientId && (
          <Select label="Client *" options={[{ value: '', label: 'Select Client' }, ...clients.map(c => ({ value: c.id, label: c.name }))]}
            value={form.clientId || ''} onChange={e => setForm(f => ({ ...f, clientId: e.target.value, applicationId: '' }))} />
        )}
        <Select label="Engagement Type *" options={ENG_TYPES}
          value={form.engagementType || ''} onChange={e => setForm(f => ({ ...f, engagementType: e.target.value, applicationId: '', name: '' }))} />
        {!usesNetworkScope(form.engagementType) && form.engagementType && clientApps.length === 0 && (user?.clientId || form.clientId) && (
          <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13, marginBottom: 16 }}>
            {appsError ? (
              <>Could not load applications for this client ({appsError}). The Application list is unavailable, so this engagement cannot be linked to an application yet.</>
            ) : (
              <>No applications found for this client. Please <strong>create an application</strong> first in the Applications page before creating this engagement.</>
            )}
          </div>
        )}
        {!usesNetworkScope(form.engagementType) && clientApps.length > 0 && (
          <Select label="Application" value={form.applicationId || ''}
            onChange={e => {
              const appId = e.target.value;
              setForm(f => ({ ...f, applicationId: appId }));
              if (appId) applyApplicationToForm(appId, clientApps);
            }}
            options={[{ value: '', label: '— Select Application —' }, ...clientApps.map(a => ({ value: a.id, label: `${a.name}${a.customer_tracking_id ? ' - ' + a.customer_tracking_id : ''}` }))]} />
        )}
        {usesNetworkScope(form.engagementType) && (
          <Input label="Engagement Name" value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Q1 2026 External Network Pentest" />
        )}
        <Select label="Environment Type" options={ENV_TYPES}
          value={form.environmentType || ''} onChange={e => setForm(f => ({ ...f, environmentType: e.target.value }))} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Select label="CREST Certification Required" options={[{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]}
            value={form.crestRequired || 'false'} onChange={e => setForm(f => ({ ...f, crestRequired: e.target.value }))} />
          <Select label="Third Party" options={[{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]}
            value={form.thirdParty ? 'true' : 'false'}
            onChange={e => setForm(f => ({ ...f, thirdParty: e.target.value === 'true' }))} />
        </div>
        {/* PCI is asked directly only for the network types. Every other type
            inherits it from the application it points at — applyApplicationToForm
            copies the application's pci_required into the form — and asking
            twice would let the two disagree. A network engagement points at no
            application, so without this the question cannot be answered at all. */}
        {usesNetworkScope(form.engagementType) && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Select label="PCI Required" options={[{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]}
              value={form.pciRequired || 'false'}
              onChange={e => setForm(f => ({ ...f, pciRequired: e.target.value }))} />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', alignSelf: 'end', paddingBottom: 10 }}>
              {form.engagementType === 'Network Segmentation'
                ? 'A segmentation test normally exists to prove a cardholder environment is separated.'
                : 'Set this when the engagement covers cardholder systems.'}
            </div>
          </div>
        )}
        {form.thirdParty && clientVendors.length > 0 && (
          <Select label="Vendor" value={form.vendorId || (clientVendors.find(v => v.is_msp_default)?.id || '')}
            onChange={e => setForm(f => ({ ...f, vendorId: e.target.value }))}
            options={clientVendors.map(v => ({ value: v.id, label: v.name + (v.is_msp_default ? ' (MSP)' : '') }))} />
        )}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.clientObligation || false}
              onChange={e => setForm(f => ({ ...f, clientObligation: e.target.checked }))} />
            Client Obligation
          </label>
          {form.clientObligation && (
            <Input label="Client This Test is for" style={{ marginTop: 12, marginBottom: 0 }}
              value={form.clientObligationFor || ''}
              onChange={e => setForm(f => ({ ...f, clientObligationFor: e.target.value }))}
              placeholder="e.g. the customer or partner requiring this test" />
          )}
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={!form.engagementType || (!user?.clientId && !form.clientId) || (!usesNetworkScope(form.engagementType) && clientApps.length > 0 && !form.applicationId)}>Create Engagement</Button>
        </div>
      </Modal>

      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Engagement" width={560} noOverlayClose>
        {deleteTarget && (
          <div>
            <div style={{ padding: 12, borderRadius: 8, background: '#7f1d1d', color: '#fecaca', fontSize: 13, marginBottom: 16 }}>
              This permanently deletes the engagement and everything attached to it. It cannot be undone.
            </div>

            <div style={{ fontSize: 14, marginBottom: 16 }}>
              <div><strong>{deleteTarget.name || '(no name)'}</strong></div>
              <div style={{ color: 'var(--text-secondary)' }}>
                {deleteTarget.client_name} · {deleteTarget.engagement_type} · {deleteTarget.status}
              </div>
            </div>

            {!deleteImpact ? (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Checking what will be removed...</p>
            ) : deleteImpact.error ? (
              <div style={{ padding: 12, borderRadius: 8, background: '#7f1d1d', color: '#fca5a5', fontSize: 13, marginBottom: 16 }}>
                Error: {deleteImpact.error}
              </div>
            ) : (
              <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg-input)', fontSize: 13, marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Will also be deleted:</div>
                {[
                  ['Findings', deleteImpact.counts.findings],
                  ['Scope items', deleteImpact.counts.scope_items],
                  ['Notes', deleteImpact.counts.notes],
                  ['Uploaded files', deleteImpact.counts.files],
                  ['Stored credentials', deleteImpact.counts.credentials],
                  ['Scan jobs', deleteImpact.counts.scan_jobs],
                  ['Generated reports', deleteImpact.counts.generated_reports],
                ].map(([label, count]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: count > 0 ? 'var(--text)' : 'var(--text-secondary)' }}>
                    <span>{label}</span><span style={{ fontWeight: count > 0 ? 600 : 400 }}>{count}</span>
                  </div>
                ))}
              </div>
            )}

            <Input label='Type DELETE to confirm' value={deleteConfirmText} placeholder="DELETE"
              onChange={e => setDeleteConfirmText(e.target.value)} />

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
              <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button variant="danger" onClick={confirmDelete}
                disabled={deleteConfirmText !== 'DELETE' || deleting || !deleteImpact || !!deleteImpact.error}>
                {deleting ? 'Deleting...' : 'Delete Engagement'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal isOpen={showImport} onClose={() => setShowImport(false)} title="Import Engagements from CSV" width={600}>
        <div style={{ marginBottom: 16 }}>
          <Button variant="ghost" onClick={downloadTemplate} style={{ padding: '6px 0', fontSize: 13, textDecoration: 'underline' }}>
            Download CSV Template
          </Button>
        </div>
        <Select label="Assign to Client *"
          options={[{ value: '', label: 'Select Client' }, ...clients.map(c => ({ value: c.id, label: c.name }))]}
          value={importClientId} onChange={e => setImportClientId(e.target.value)} />
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>CSV File *</label>
          <input type="file" accept=".csv" onChange={e => setImportFile(e.target.files[0])}
            style={{ fontSize: 14, color: 'var(--text)' }} />
        </div>
        {importResult && (
          <div style={{
            padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13,
            background: importResult.error ? '#7f1d1d' : '#14532d',
            color: importResult.error ? '#fca5a5' : '#86efac'
          }}>
            {importResult.error ? (
              <div>Error: {importResult.error}</div>
            ) : (
              <div>
                <div>Successfully imported {importResult.imported} engagement{importResult.imported !== 1 ? 's' : ''}.</div>
                {importResult.errors?.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontWeight: 600 }}>Errors:</div>
                    {importResult.errors.map((e, i) => (
                      <div key={i}>Row {e.row}: {e.error}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setShowImport(false)}>Close</Button>
          <Button onClick={handleImport} disabled={!importClientId || !importFile || importing}>
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
